/**
 * PR-18 — Dynamic Match Poll Engine.
 * Context-aware in-memory polls about the live game (no DB writes). Used as
 * a fallback/augment on top of admin-published live polls so the demo always
 * has questions flowing without manual setup.
 */
import type { PollWithOptions } from "@/lib/polls/pollsService";

export type MatchPhase = "early" | "mid" | "late" | "halftime" | "stoppage" | "post";

export interface MatchPollContext {
  minute: number;
  phase: MatchPhase;
  scoreHome: number;
  scoreAway: number;
  homeName?: string;
  awayName?: string;
  recentEvent?: "goal" | "yellow" | "red" | "var" | "corner" | "shot" | "sub" | null;
  dangerActive?: boolean;
}

interface SeedPoll {
  id: string;
  question: string;
  options: string[];
  weight: (ctx: MatchPollContext) => number;
}

const SEED_POLLS: SeedPoll[] = [
  { id: "engine_goal_5m", question: "¿Habrá gol en los próximos 5 minutos?", options: ["Sí", "No"],
    weight: (c) => (c.minute > 5 && !c.dangerActive ? 1.2 : 0.4) },
  { id: "engine_first_scorer", question: "¿Quién anotará primero?", options: ["Local", "Visitante", "Ya hubo gol"],
    weight: (c) => (c.scoreHome + c.scoreAway === 0 && c.minute < 60 ? 1.5 : 0.2) },
  { id: "engine_yellow", question: "¿Habrá tarjeta amarilla en los próximos minutos?", options: ["Sí", "No"],
    weight: (c) => (c.phase === "mid" || c.phase === "late" ? 1.0 : 0.6) },
  { id: "engine_penal", question: "¿Habrá penalti en este partido?", options: ["Sí", "No"],
    weight: (c) => (c.recentEvent === "var" ? 1.6 : 0.8) },
  { id: "engine_sub", question: "¿Habrá sustitución en los próximos 10 minutos?", options: ["Sí", "No"],
    weight: (c) => (c.minute >= 55 ? 1.3 : 0.4) },
  { id: "engine_var", question: "¿Habrá revisión VAR antes del final?", options: ["Sí", "No"],
    weight: () => 0.7 },
  { id: "engine_shot", question: "¿Habrá tiro al arco en los próximos 3 minutos?", options: ["Sí", "No"],
    weight: (c) => (c.recentEvent === "corner" || c.recentEvent === "shot" ? 1.4 : 0.9) },
  { id: "engine_corner", question: "¿Habrá tiro de esquina en los próximos 5 minutos?", options: ["Sí", "No"],
    weight: () => 1.0 },
];

export function resolveMatchPhase(minute: number, isHalftime = false, isPost = false): MatchPhase {
  if (isPost) return "post";
  if (isHalftime || (minute >= 45 && minute <= 46)) return "halftime";
  if (minute > 90) return "stoppage";
  if (minute < 25) return "early";
  if (minute < 70) return "mid";
  return "late";
}

export function pickNextMatchPoll(ctx: MatchPollContext, recentlyShown: string[] = []): PollWithOptions | null {
  if (ctx.dangerActive) return null;
  const recent = new Set(recentlyShown);
  const candidates = SEED_POLLS.filter((p) => !recent.has(p.id));
  const pool = (candidates.length > 0 ? candidates : SEED_POLLS).map((p) => ({ poll: p, w: Math.max(0.05, p.weight(ctx)) }));
  const total = pool.reduce((acc, x) => acc + x.w, 0);
  let r = Math.random() * total;
  let chosen = pool[0].poll;
  for (const entry of pool) {
    if ((r -= entry.w) <= 0) { chosen = entry.poll; break; }
  }
  const now = Date.now();
  const id = `${chosen.id}-${now}`;
  const nowIso = new Date(now).toISOString();
  const poll: PollWithOptions = {
    id,
    tenant_id: null,
    title: chosen.question,
    description: null,
    question: chosen.question,
    status: "live",
    reward_type: "no_reward",
    reward_value: 0,
    starts_at: nowIso,
    ends_at: new Date(now + 60_000).toISOString(),
    countries: [],
    target_segments: [],
    sponsor: null,
    created_at: nowIso,
    updated_at: nowIso,
    options: chosen.options.map((label, idx) => ({
      id: `${id}-opt-${idx}`,
      poll_id: id,
      label,
      sort_order: idx,
    })),
  };
  return poll;
}

export const MATCH_POLL_SEED_IDS = SEED_POLLS.map((p) => p.id);